import { gql, useLazyQuery } from '@apollo/client';
import { openPDF } from '@/utils/navigation';
import {
  OrderGuide_GetParSheetPdfQuery,
  OrderGuide_GetParSheetPdfQueryVariables,
} from '@/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useOrderGuideSettingsContext } from '@/contexts/order-guide-settings-context';
import { useState } from 'react';
import { useToasts } from '@/contexts/toast-context';

export const GET_PAR_SHEET_PDF = gql`
  query OrderGuide_GetParSheetPdf(
    $collectionUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $sortOrder: OrderGuideSortOrder
  ) {
    getParSheetPdf(
      collection_id: $collectionUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      sort_order: $sortOrder
    ) {
      url
    }
  }
`;

export const useOpenParSheetPDF = () => {
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useAuthenticatedUserContext();
  const { orderGuideSortOrder } = useOrderGuideSettingsContext();
  const [loadingParSheetPDF, setLoadingParSheetPDF] = useState(false);

  const [getParSheetPDF] = useLazyQuery<
    OrderGuide_GetParSheetPdfQuery,
    OrderGuide_GetParSheetPdfQueryVariables
  >(GET_PAR_SHEET_PDF);

  const openParSheetPDF = async (collectionUUID: string) => {
    try {
      setLoadingParSheetPDF(true);

      await openPDF(async () => {
        const { data } = await getParSheetPDF({
          fetchPolicy: 'network-only',
          variables: {
            collectionUUID,
            restaurantUUID,
            sortOrder: orderGuideSortOrder,
            supplierUUID,
          },
        });

        const pdfURL = data?.getParSheetPdf.url;
        if (!pdfURL) {
          throw new Error('Unable to retrieve PAR sheet PDF');
        }

        return pdfURL;
      });
    } catch (e) {
      showErrorToast();
    } finally {
      setLoadingParSheetPDF(false);
    }
  };

  return {
    loadingParSheetPDF,
    openParSheetPDF,
  };
};
